import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { UserIcon, KeyIcon, BellIcon, Cog6ToothIcon, CurrencyDollarIcon, ChartBarIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const ProfilePage = () => {
  const { t } = useTranslation('profile')
  const { t: ts } = useTranslation('settings')
  const { user, updateProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')

  // Formularz profilu
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: ''
  })

  // Formularz zmiany hasła
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })

  // Ustawienia powiadomień
  const [notificationSettings, setNotificationSettings] = useState({
    budget_notifications: true,
    email_notifications: true,
    expense_reminders: false
  })

  const [preferencesSettings, setPreferencesSettings] = useState({
    language: 'pl',
    currency: 'PLN',
    timezone: 'Europe/Warsaw'
  })

  // Ustawienia budżetu
  const [budgetSettings, setBudgetSettings] = useState({
    default_budget_period: 'monthly',
    budget_notifications: true,
    expense_categories_required: false,
    auto_budget_alerts: true
  })

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        username: user.username || ''
      })

      if (user.preferences) {
        setNotificationSettings({
          budget_notifications: user.preferences.budget_notifications || false,
          email_notifications: user.preferences.email_notifications || false,
          expense_reminders: user.preferences.expense_reminders || false
        })
        setPreferencesSettings({
          language: user.preferences.language || 'pl',
          currency: user.preferences.currency || 'PLN',
          timezone: user.preferences.timezone || 'Europe/Warsaw'
        })
        setBudgetSettings({
          default_budget_period: user.preferences.default_budget_period || 'monthly',
          budget_notifications: user.preferences.budget_notifications ?? true,
          expense_categories_required: user.preferences.expense_categories_required ?? false,
          auto_budget_alerts: user.preferences.auto_budget_alerts ?? true
        })
      }
    }
  }, [user])

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await updateProfile(profileForm)
      toast.success(t('toast.profileUpdated'))
    } catch (error) {
      toast.error(t('toast.profileError'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t('password.mismatch'))
      return
    }

    if (passwordForm.new_password.length < 8) {
      toast.error(t('password.minLength'))
      return
    }

    setLoading(true)

    try {
      // TODO: Implementacja zmiany hasła w API
      toast.success(t('toast.passwordChanged'))
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
    } catch (error) {
      toast.error(t('toast.passwordError'))
    } finally {
      setLoading(false)
    }
  }

  const handleNotificationSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await updateProfile({ preferences: { ...user.preferences, ...notificationSettings } })
      toast.success(t('toast.notificationsUpdated'))
    } catch (error) {
      toast.error(t('toast.notificationsError'))
    } finally {
      setLoading(false)
    }
  }

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await updateProfile({ preferences: { ...user.preferences, ...preferencesSettings } })
      toast.success(t('preferences.success'))
    } catch (error) {
      toast.error(t('preferences.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleBudgetSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await updateProfile({ preferences: { ...user.preferences, ...budgetSettings } })
      toast.success(ts('budgetSettings.success'))
    } catch (error) {
      toast.error(ts('budgetSettings.error'))
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'profile', name: t('tabs.profile'), icon: UserIcon },
    { id: 'password', name: t('tabs.password'), icon: KeyIcon },
    { id: 'notifications', name: t('tabs.notifications'), icon: BellIcon },
    { id: 'preferences', name: t('tabs.preferences'), icon: Cog6ToothIcon },
    { id: 'budget', name: ts('tabs.budget'), icon: CurrencyDollarIcon },
    { id: 'data', name: ts('tabs.data'), icon: ChartBarIcon },
  ]

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" text={t('loading')} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--color-border)]">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-[#1f9f93] text-[#1f9f93]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{t('info.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t('info.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handleProfileSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('info.firstName')}
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('info.lastName')}
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('info.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('info.username')}
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={profileForm.username}
                    onChange={(e) => setProfileForm({...profileForm, username: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                  {loading ? t('savingChanges') : t('saveChanges')}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'password' && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{t('password.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t('password.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div>
                <label htmlFor="current_password" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('password.current')}
                </label>
                <input
                  type="password"
                  id="current_password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  required
                />
              </div>

              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('password.new')}
                </label>
                <input
                  type="password"
                  id="new_password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  required
                  minLength={8}
                />
                <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t('password.minLength')}</p>
              </div>

              <div>
                <label htmlFor="confirm_password" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('password.confirm')}
                </label>
                <input
                  type="password"
                  id="confirm_password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  required
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                  {loading ? t('password.changing') : t('password.submit')}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'notifications' && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{t('notificationsSection.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t('notificationsSection.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handleNotificationSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{t('notificationsSection.budget')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{t('notificationsSection.budgetDesc')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.budget_notifications}
                    onChange={(e) => setNotificationSettings({...notificationSettings, budget_notifications: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{t('notificationsSection.email')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{t('notificationsSection.emailDesc')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.email_notifications}
                    onChange={(e) => setNotificationSettings({...notificationSettings, email_notifications: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{t('notificationsSection.reminders')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{t('notificationsSection.remindersDesc')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.expense_reminders}
                    onChange={(e) => setNotificationSettings({...notificationSettings, expense_reminders: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                  {loading ? t('savingChanges') : t('saveSettings')}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'preferences' && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{t('preferences.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t('preferences.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handlePreferencesSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('preferences.language')}
                  </label>
                  <select
                    id="language"
                    value={preferencesSettings.language}
                    onChange={(e) => setPreferencesSettings({...preferencesSettings, language: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  >
                    <option value="pl">Polski</option>
                    <option value="en">English</option>
                    <option value="de">Deutsch</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="currency" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('preferences.currency')}
                  </label>
                  <select
                    id="currency"
                    value={preferencesSettings.currency}
                    onChange={(e) => setPreferencesSettings({...preferencesSettings, currency: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  >
                    <option value="PLN">{t('preferences.currencies.PLN')}</option>
                    <option value="EUR">{t('preferences.currencies.EUR')}</option>
                    <option value="USD">{t('preferences.currencies.USD')}</option>
                    <option value="GBP">{t('preferences.currencies.GBP')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-[var(--color-text)]">
                    {t('preferences.timezone')}
                  </label>
                  <select
                    id="timezone"
                    value={preferencesSettings.timezone}
                    onChange={(e) => setPreferencesSettings({...preferencesSettings, timezone: e.target.value})}
                    className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                  >
                    <option value="Europe/Warsaw">{t('preferences.timezones.Europe/Warsaw')}</option>
                    <option value="Europe/London">{t('preferences.timezones.Europe/London')}</option>
                    <option value="Europe/Berlin">{t('preferences.timezones.Europe/Berlin')}</option>
                    <option value="America/New_York">{t('preferences.timezones.America/New_York')}</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                  {loading ? t('savingChanges') : t('saveChanges')}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'budget' && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{ts('budgetSettings.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{ts('budgetSettings.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handleBudgetSubmit} className="space-y-6">
              <div>
                <label htmlFor="default_budget_period" className="block text-sm font-medium text-[var(--color-text)]">
                  {ts('budgetSettings.defaultPeriod')}
                </label>
                <select
                  id="default_budget_period"
                  value={budgetSettings.default_budget_period}
                  onChange={(e) => setBudgetSettings({...budgetSettings, default_budget_period: e.target.value})}
                  className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                >
                  <option value="weekly">{t('common:periods.weekly', 'Tygodniowy')}</option>
                  <option value="monthly">{t('common:periods.monthly', 'Miesięczny')}</option>
                  <option value="quarterly">{t('common:periods.quarterly', 'Kwartalny')}</option>
                  <option value="yearly">{t('common:periods.yearly', 'Roczny')}</option>
                </select>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{ts('budgetSettings.notifications')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{ts('budgetSettings.notificationsDescription')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={budgetSettings.budget_notifications}
                    onChange={(e) => setBudgetSettings({...budgetSettings, budget_notifications: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{ts('budgetSettings.requireCategories')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{ts('budgetSettings.requireCategoriesDescription')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={budgetSettings.expense_categories_required}
                    onChange={(e) => setBudgetSettings({...budgetSettings, expense_categories_required: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{ts('budgetSettings.autoAlerts')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{ts('budgetSettings.autoAlertsDescription')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={budgetSettings.auto_budget_alerts}
                    onChange={(e) => setBudgetSettings({...budgetSettings, auto_budget_alerts: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                  {loading ? t('savingChanges') : t('saveChanges')}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'data' && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{ts('data.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{ts('data.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <div className="space-y-6">
              <div className="border-l-4 border-blue-400 bg-[var(--color-info-bg)] p-4">
                <p className="text-sm text-[var(--color-info-text)]">
                  <strong>{ts('data.exportTitle')}:</strong> {ts('data.exportDescription')}
                </p>
              </div>

              <div className="flex space-x-4">
                <Button variant="outline">{ts('data.exportAll')}</Button>
                <Button variant="outline">{ts('data.exportExpenses')}</Button>
                <Button variant="outline">{ts('data.exportDocuments')}</Button>
              </div>

              <hr className="border-[var(--color-border)]" />

              <div className="border-l-4 border-red-400 bg-[var(--color-error-bg)] p-4">
                <p className="text-sm text-[var(--color-error-text)]">
                  <strong>{ts('data.warning')}:</strong> {ts('data.deleteWarning')}
                </p>
              </div>

              <div className="flex justify-start">
                <Button variant="danger">{ts('data.deleteAccount')}</Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default ProfilePage
